/**
 * Player API Response Schemas
 *
 * Output schemas for Player API responses.
 * Includes product data, services, accommodations, and available values.
 */

import { z } from 'zod';
import {
  ProcessMessageSchema,
  CountrySchema,
  RegionSchema,
  DestinationSchema,
  CitySchema,
  IdentitySchema,
  ServiceReferenceSchema,
  PriceInformationSchema,
} from './common';

// ----- Product and Service Schemas -----

/**
 * Player API Product schema
 *
 * IMPORTANT: This schema is used in different contexts with different semantic meanings:
 * - In GetProductList response: Represents a hotel/accommodation product
 * - In GetProductOffers response: Represents a specific priced offer
 *
 * Use the type aliases below for clarity in your code:
 * - PlayerSearchProduct: For products from search results
 * - PlayerProductOffer: For offers with pricing
 */
export const ProductSchema = z.object({
  Id: z.number(),
  RequestBooking: z.boolean().optional(),
  ServiceReferences: z.object({
    ServiceReference: z.array(ServiceReferenceSchema),
  }),
  PriceInformation: PriceInformationSchema,
  Availability: z.number().optional(),
});

export const RoomSchema = z.object({
  RoomTypes: z.object({
    RoomType: z.array(z.string()),
  }),
  OpCode: z.string(),
  RequestCode: z.string(),
  RoomAttributes: z.unknown().nullable().optional(),
  Description: z.string(),
  Code: z.string(),
  ShortDescription: z.string().nullable().optional(),
});

// Board type information (meal plan)
export const BoardSchema = z.object({
  BoardType: z.string(),
  Name: z.string(),
  Code: z.string(),
});

/**
 * Accommodation-specific information
 *
 * Contains the actual product identifier (GIATA ID) and hotel details
 */
export const AccommodationSpecificSchema = z.object({
  Id: z.number(),
  Name: z.string(),
  /**
   * Product Code (GIATA ID) - unique identifier for the hotel/property
   * @example "HERIRINI119", "HTLPAR001"
   */
  ProductCode: z.string(),
  TourOperatorCode: z.string(),
  AccommodationAttributes: z.unknown().nullable().optional(),
  GiataFacts: z.unknown().nullable().optional(),
  ProductGroups: z.unknown().nullable().optional(),
});

export const LocationSchema: z.ZodType<{
  Airports: string;
  Country: { Code: string; Name: string };
  Region: { Code: string; Name: string };
  City?: { Name: string } | null;
  GeoCoordinate?: unknown | null;
  Destination: { Code: string; Name: string };
}> = z.lazy(() =>
  z.object({
    Airports: z.string(),
    Country: CountrySchema,
    Region: RegionSchema,
    City: CitySchema.nullable().optional(),
    GeoCoordinate: z.unknown().nullable().optional(),
    Destination: DestinationSchema,
  })
);

export const AccommodationVariantSchema = z.object({
  Id: z.number(),
  Start: z.string(),
  End: z.string(),
  SpecificIdRef: z.number(),
  Rooms: z.object({
    Room: z.array(RoomSchema),
  }),
  Boards: z.object({
    Board: z.array(BoardSchema),
  }),
});

export const AccommodationServiceSchema = z.object({
  Identities: z.object({
    Identity: z.array(IdentitySchema),
  }),
  Location: LocationSchema,
  AccommodationSpecifics: z.object({
    AccommodationSpecific: z.array(AccommodationSpecificSchema),
  }),
  AccommodationVariants: z.object({
    AccommodationVariant: z.array(AccommodationVariantSchema),
  }),
});

export const ServiceSchema = z.object({
  Id: z.number(),
  AccommodationService: AccommodationServiceSchema,
});

// ----- Dynamic filter narrowing (Filters element on GetProductList) -----

export const AttributeCountSchema = z.object({
  Attribute: z.string().optional(),
  Count: z.number().int().optional(),
});

export const DynamicFiltersSchema = z.object({
  AttributesOverview: z
    .object({ Attribute: z.array(AttributeCountSchema) })
    .nullable()
    .optional(),
});

// ----- GetProductList Response -----

export const GetProductListResultSchema = z.object({
  RequestId: z.string().optional(),
  Currency: z.string().optional(),
  Lang: z.string().optional(),
  Success: z.boolean(),
  ProcessMessages: z
    .object({
      ProcessMessage: z.array(ProcessMessageSchema),
    })
    .optional(),
  ResultsFrom: z.string().optional(),
  ResultsTo: z.string().optional(),
  ResultsTotal: z.string(),
  SortedBy: z.string().optional(),
  Products: z
    .object({
      Product: z.array(ProductSchema),
    })
    .nullable()
    .optional(),
  Services: z
    .object({
      Service: z.array(ServiceSchema),
    })
    .nullable()
    .optional(),
  Filters: DynamicFiltersSchema.nullable().optional(),
});

// ----- GetProductOffers Response -----

export const GetProductOffersResultSchema = z.object({
  RequestId: z.string().optional(),
  Currency: z.string().optional(),
  Lang: z.string().optional(),
  Success: z.boolean(),
  ProcessMessages: z
    .object({
      ProcessMessage: z.array(ProcessMessageSchema),
    })
    .optional(),
  ResultsFrom: z.string().optional(),
  ResultsTo: z.string().optional(),
  ResultsTotal: z.string(),
  SortedBy: z.string().optional(),
  Products: z
    .object({
      Product: z.array(ProductSchema),
    })
    .nullable()
    .optional(),
  Services: z
    .object({
      Service: z.array(ServiceSchema),
    })
    .nullable()
    .optional(),
});

// ----- GetAvailableValues Response -----

export const ProductGroupSchema: z.ZodType<{
  Id: number;
  CountProductsInGroup: number;
  Name: string;
  ProductGroups?: {
    ProductGroup: Array<{
      Id: number;
      CountProductsInGroup: number;
      Name: string;
      ProductGroups?: unknown;
    }>;
  };
}> = z.lazy(() =>
  z.object({
    Id: z.number(),
    CountProductsInGroup: z.number(),
    Name: z.string(),
    ProductGroups: z
      .object({
        ProductGroup: z.array(ProductGroupSchema),
      })
      .optional(),
  })
);

export const GeoValueSchema: z.ZodType<{
  Code?: string;
  Name: string;
  Descendants?: {
    Type: 'Country' | 'Region' | 'Destination' | 'City';
    Values: {
      Value: Array<{
        Code?: string;
        Name: string;
        Descendants?: unknown;
      }>;
    };
  };
}> = z.lazy(() =>
  z.object({
    Code: z.string().optional(),
    Name: z.string(),
    Descendants: z
      .object({
        Type: z.enum(['Country', 'Region', 'Destination', 'City']),
        Values: z.object({
          Value: z.array(GeoValueSchema),
        }),
      })
      .optional(),
  })
);

export const GeoLocationsSchema = z.object({
  Type: z.literal('Country'),
  Values: z.object({
    Value: z.array(GeoValueSchema),
  }),
});

export const HotelSchema = z.object({
  ProductCode: z.string(),
  Name: z.string(),
});

// ----- GetFilters Response -----

/**
 * One GetFilters answer. `CheckInDates` fills the `TravelStartDates*` fields,
 * `Duration` fills `Attributes` with `Durations.N`. The arrival mask is read
 * from the raw XML instead: the parser turns the `0/1` string into a number.
 */
export const GetFiltersResultSchema = z.object({
  Success: z.boolean(),
  Filter: z.string().optional(),
  TravelStartDatesFrom: z.string().optional(),
  Attributes: z
    .object({ Attribute: z.union([AttributeCountSchema, z.array(AttributeCountSchema)]) })
    .nullable()
    .optional(),
});

export const GetAvailableValuesResultSchema = z.object({
  RequestId: z.string().optional(),
  Currency: z.string().optional(),
  Success: z.boolean(),
  ProcessMessages: z
    .object({
      ProcessMessage: z.array(ProcessMessageSchema),
    })
    .optional(),
  ArrivalAirports: z
    .object({
      Airport: z.array(z.string()),
    })
    .optional(),
  Countries: z
    .object({
      Country: z.array(CountrySchema),
    })
    .optional(),
  Regions: z
    .object({
      Region: z.array(RegionSchema),
    })
    .optional(),
  ProductGroups: z
    .object({
      ProductGroup: z.array(ProductGroupSchema),
    })
    .optional(),
  AccommodationProperties: z
    .object({
      AccommodationProperty: z.array(z.string()),
    })
    .optional(),
  MinDate: z.string().optional(),
  MaxDate: z.string().optional(),
  HotelNames: z
    .object({
      HotelName: z.array(z.string()),
    })
    .optional(),
  Cities: z
    .object({
      City: z.array(z.string()),
    })
    .optional(),
  RoomTypes: z
    .object({
      RoomType: z.array(z.string()),
    })
    .optional(),
  BoardTypes: z
    .object({
      BoardType: z.array(z.string()),
    })
    .optional(),
  GeoLocations: GeoLocationsSchema.optional(),
  TouroperatorCodes: z
    .object({
      TouroperatorCode: z.array(z.string()),
    })
    .optional(),
  Destinations: z
    .object({
      Destination: z.array(DestinationSchema),
    })
    .optional(),
  Durations: z
    .object({
      Duration: z.array(z.number().int().positive()),
    })
    .optional(),
  Cruise: z
    .object({
      Ports: z.unknown(),
    })
    .optional(),
  Hotels: z
    .object({
      Hotel: z.array(HotelSchema),
    })
    .optional(),
  PriceTypes: z
    .object({
      PriceType: z.array(z.string()),
    })
    .optional(),
  SubRegions: z.unknown().optional(),
});

// ----- Type Exports -----

/**
 * Player API Product type - context-dependent meaning
 *
 * This type is returned by Player API in different contexts:
 * - GetProductList: Represents a hotel/accommodation product
 * - GetProductOffers: Represents a specific priced offer
 *
 * @see PlayerSearchProduct for products from search results
 * @see PlayerProductOffer for offers with specific pricing
 */
export type Product = z.infer<typeof ProductSchema>;

/**
 * Product from GetProductList endpoint
 *
 * Represents a hotel/accommodation without specific pricing.
 * Used for search results and product listings.
 */
export type PlayerSearchProduct = Product;

/**
 * Product from GetProductOffers endpoint
 *
 * Represents a specific priced offer with dates and room/board configuration.
 *
 * IMPORTANT: Despite being called "Product" by the API, this is actually an Offer.
 * We map this to MappedOffer internally for clarity.
 *
 * The Product.Id field contains the unique offer ID (numeric).
 * The actual product identifier (GIATA ID) is found in:
 * Service.AccommodationService.AccommodationSpecifics.AccommodationSpecific[].ProductCode
 */
export type PlayerProductOffer = Product;

export type Room = z.infer<typeof RoomSchema>;
export type Board = z.infer<typeof BoardSchema>;
export type AccommodationSpecific = z.infer<typeof AccommodationSpecificSchema>;
export type Location = z.infer<typeof LocationSchema>;
export type AccommodationVariant = z.infer<typeof AccommodationVariantSchema>;
export type AccommodationService = z.infer<typeof AccommodationServiceSchema>;
export type Service = z.infer<typeof ServiceSchema>;
export type AttributeCount = z.infer<typeof AttributeCountSchema>;
export type DynamicFilters = z.infer<typeof DynamicFiltersSchema>;
export type GetProductListResult = z.infer<typeof GetProductListResultSchema>;
export type GetProductOffersResult = z.infer<typeof GetProductOffersResultSchema>;
export type ProductGroup = z.infer<typeof ProductGroupSchema>;
export type GeoValue = z.infer<typeof GeoValueSchema>;
export type GeoLocations = z.infer<typeof GeoLocationsSchema>;
export type Hotel = z.infer<typeof HotelSchema>;
export type GetAvailableValuesResult = z.infer<typeof GetAvailableValuesResultSchema>;
export type GetFiltersResult = z.infer<typeof GetFiltersResultSchema>;
